import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'underscore';
import classnames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import {
  useDefaultEditorStyle,
  getThumbnailUrlForFacebookPhoto,
} from '@bingads-webui-campaign-react/smart-component-util';

import { SelectImagesListView } from './select-images-list-view';
import { ImageItemView } from './image-item-view';
import { usePivotImageEditorContext } from '../smart-pivot-image-editor-context';

const getStyles = () => ({
  placeholderMsg: {
    paddingBottom: 32,
    textAlign: 'center',
  },
});

const UnStyledFacebookPhotosListView = ({
  i18n,
  onItemSelectionChanged,
  items,
  isFbSupported,
  classes,
  gotoSettings,
  imageWidth,
  imageHeight,
  pageHeight,
  className,
  itemsPerPage,
  isMultiple,
  shouldResetSelection,
  itemToUnselect,
  resetItemToUnselect,
}) => {
  const {
    infoMessageBarClassName,
  } = useDefaultEditorStyle();
  const noData = isEmpty(items);
  const onRenderImageItem = item => (
    <ImageItemView src={getThumbnailUrlForFacebookPhoto(item)} />
  );

  const { selectionIds } = usePivotImageEditorContext();
  const finalItems = useMemo(() => items.map(item => ({
    ...item,
    selected: selectionIds.includes(item.Id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [items]);

  return (
    <div className={className}>
      {
        noData ?
          (
            <div className={classnames(classes.placeholderMsg, 'smart-page-gallery-no-fb-photos-msg')}>
              {isFbSupported ?
                <MessageBar isMultiline className={infoMessageBarClassName}>
                  {i18n.getString(_TL_('There are no Facebook images to import.'))}
                </MessageBar> :
                <PrimaryButton
                  onClick={gotoSettings}
                  text={i18n.getString(_TL_('Connect my Facebook page'))}
                />
              }
            </div>
          )
          :
          (
            <SelectImagesListView
              items={finalItems}
              pageHeight={pageHeight}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              onRenderImageItem={onRenderImageItem}
              onItemSelectionChanged={onItemSelectionChanged}
              selectable
              isMultiple={isMultiple}
              showPagination
              i18n={i18n}
              itemsPerPage={itemsPerPage}
              shouldResetSelection={shouldResetSelection}
              itemToUnselect={itemToUnselect}
              resetItemToUnselect={resetItemToUnselect}
            />
          )
      }
    </div>
  );
};

UnStyledFacebookPhotosListView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    imageId: PropTypes.string,
  })),
  onItemSelectionChanged: PropTypes.func,
  isFbSupported: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    placeholderMsg: PropTypes.string.isRequired,
  }).isRequired,
  gotoSettings: PropTypes.func.isRequired,
  imageWidth: PropTypes.number.isRequired,
  pageHeight: PropTypes.number.isRequired,
  imageHeight: PropTypes.number.isRequired,
  className: PropTypes.string,
  itemsPerPage: PropTypes.number,
  isMultiple: PropTypes.bool,
  shouldResetSelection: PropTypes.bool,
  itemToUnselect: PropTypes.shape({}),
  resetItemToUnselect: PropTypes.func,
};

UnStyledFacebookPhotosListView.defaultProps = {
  items: [],
  className: null,
  itemsPerPage: 6,
  onItemSelectionChanged: noop,
  isMultiple: true,
  shouldResetSelection: false,
  itemToUnselect: null,
  resetItemToUnselect: noop,
};

export const FacebookPhotosListView = withDefaultStyles(
  UnStyledFacebookPhotosListView,
  getStyles
);
